import React, { useCallback, useEffect, useState } from 'react'
import {
    Box, Typography, Button, TextField, InputAdornment, Divider,
    IconButton, Collapse, Chip, CircularProgress, MenuItem, Select,
    FormControl, Tooltip, Switch, Skeleton, Autocomplete,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import PublicIcon from '@mui/icons-material/Public'
import CheckIcon from '@mui/icons-material/Check'
import RefreshIcon from '@mui/icons-material/Refresh'
import SearchIcon from '@mui/icons-material/Search'
import TravelExploreIcon from '@mui/icons-material/TravelExplore'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import PopupDialog from '../../Components/Popups/ConfirmDialog'
import {
    ApiListPlatformFees,
    ApiCreatePlatformFee,
    ApiUpdatePlatformFee,
    ApiUpdatePlatformFeeStatus,
    ApiDeletePlatformFee,
    ApiResolvePlatformFee,
    ApiGetCountries,
    ApiGetStates,
} from '../../apiService/Core/Apicall'

interface PlatformFee {
    _id: string
    countryCode: string
    stateCode: string | null
    feeType: 'percentage' | 'fixed'
    fee: number
    currency: string | null
    isActive: boolean
    createdAt: string
    updatedAt: string
}

interface CountryGroup {
    countryCode: string
    countryName: string
    defaultFee: PlatformFee | null
    stateFees: PlatformFee[]
}

interface Country { alpha2: string; name: string }
interface StateOption { code: string; name: string }

type FeeFormData = {
    countryCode: string
    stateCode: string
    feeType: 'percentage' | 'fixed'
    fee: string
    currency: string
}

type DeleteTarget = { fee: PlatformFee; label: string }

// const CURRENCIES = ['USD', 'AUD']

const feeLabel = (f: PlatformFee) =>
    f.feeType === 'percentage'
        ? `${f.fee}%`
        : `${f.currency ?? '$'}${f.fee}`

const buildQS = (params: Record<string, any>) => {
    const p = Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
        .join('&')
    return p ? '?' + p : ''
}


const fieldStyle = (accent = '#f2674a') => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px', background: '#181818', color: '#e0e0e0', fontSize: 15,
        '& fieldset': { borderColor: '#2f2f2f' },
        '&:hover fieldset': { borderColor: accent + '60' },
        '&.Mui-focused fieldset': { borderColor: accent },
    },
    '& .MuiFormHelperText-root': { color: '#ef4444', fontSize: 12, mx: 0 },
    '& .MuiSelect-icon': { color: '#5a5a5a' },
    '& .MuiInputBase-input': { fontSize: 15, py: '10px' },
})

const labelSx = {
    fontSize: 12, color: '#914141',
    letterSpacing: '0.08em', textTransform: 'uppercase' as const, mb: 0.8,
    fontWeight: 600,
}


const ResolvePanel: React.FC<{ countries: Country[] }> = ({ countries }) => {
    const [cc, setCc] = useState('')
    const [states, setStates] = useState<StateOption[]>([])
    const [sc, setSc] = useState('')
    const [loadingStates, setLoadingStates] = useState(false)
    const [result, setResult] = useState<any>(null)
    const [resolving, setResolving] = useState(false)
    const [open, setOpen] = useState(false)

    const loadStates = async (code: string) => {
        setCc(code); setSc(''); setResult(null)
        if (!code) return
        setLoadingStates(true)
        try {
            const res = await ApiGetStates(code)
            if (res?.status) setStates(res.data ?? [])
        } catch { } finally { setLoadingStates(false) }
    }

    const handleResolve = async () => {
        if (!cc) return
        setResolving(true); setResult(null)
        try {
            const res = await ApiResolvePlatformFee(cc, sc || undefined)
            if (res?.status) setResult(res.data)
        } catch { } finally { setResolving(false) }
    }

    return (
        <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Button
                    size="medium"
                    startIcon={<TravelExploreIcon sx={{ fontSize: 17 }} />}
                    onClick={() => setOpen(o => !o)}
                    sx={{
                        color: '#3b82f6', fontSize: 13, fontWeight: 700,
                        border: '1px dashed #3b82f640', borderRadius: '10px', px: 2.5, py: 1,
                        '&:hover': { background: '#3b82f60a', borderColor: '#3b82f670' },
                    }}
                >
                    Test Effective Fee
                </Button>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <InfoOutlinedIcon sx={{ fontSize: 15, color: '#3a3a3a' }} />
                    <Typography sx={{ fontSize: 12, color: '#737171' }}>
                        Preview which fee applies to a user based on their country &amp; state
                    </Typography>
                </Box>
            </Box>

            <Collapse in={open} timeout={180}>
                <Box sx={{
                    mt: 2, p: 3, borderRadius: '12px',
                    background: '#0e1825', border: '1px solid #3b82f625',
                }}>
                    {/* <Box sx={{
                        mb: 2.5, p: 1.5, borderRadius: '8px',
                        background: '#3b82f608', border: '1px solid #3b82f618',
                    }}>
                        <Typography sx={{ fontSize: 13, color: '#4a90d9', lineHeight: 1.6 }}>
                            <strong>How it works:</strong> Fees are checked in order — State → Country → Global default.
                            If a state-level fee exists, it wins. Otherwise the country fee applies. If neither is set, the global fee kicks in.
                            Use this tool to confirm the exact fee a user in a specific location would see.
                        </Typography>
                    </Box> */}

                    <Typography sx={{ ...labelSx, color: '#3b82f6', mb: 2 }}>
                        Select a location to preview
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <Box sx={{ flex: '1 1 180px' }}>
                            <Typography sx={labelSx}>Country</Typography>
                            <Autocomplete
                                size="small"
                                options={countries}
                                getOptionLabel={c => c.name}
                                value={countries.find(c => c.alpha2 === cc) ?? null}
                                onChange={(_, val) => loadStates(val?.alpha2 ?? '')}
                                renderOption={(props, c) => (
                                    <li {...props} key={c.alpha2}>
                                        <span style={{ color: '#5a5a5a', fontSize: 11, marginRight: 8, minWidth: 32 }}>{(c.alpha2)}</span>{c.name}
                                    </li>
                                )}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        placeholder="Search country…"
                                        sx={acSx('#3b82f6')}
                                        // InputProps={{
                                        //     ...params.InputProps,
                                        //     startAdornment: cc
                                        //         ? <span style={{ fontSize: 18, marginRight: 6, flexShrink: 0 }}>{(cc)}</span>
                                        //         : undefined,
                                        // }}
                                    />
                                )}
                                slotProps={{ paper: { sx: acPaperSx } }}
                                noOptionsText={<span style={{ color: '#555', fontSize: 13 }}>No country found</span>}
                            />
                        </Box>

                        <Box sx={{ flex: '1 1 180px' }}>
                            <Typography sx={labelSx}>
                                State
                            </Typography>
                            <Autocomplete
                                size="small"
                                options={states}
                                getOptionLabel={s => s.name}
                                value={states.find(s => s.code === sc) ?? null}
                                disabled={!cc || loadingStates}
                                loading={loadingStates}
                                onChange={(_, val) => { setSc(val?.code ?? ''); setResult(null) }}
                                renderOption={(props, s) => (
                                    <li {...props} key={s.code}>
                                        <span style={{ color: '#5a5a5a', fontSize: 11, marginRight: 8, minWidth: 32 }}>{s.code}</span>
                                        {s.name}
                                    </li>
                                )}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        placeholder={!cc ? 'Select country first' : loadingStates ? 'Loading…' : 'Search state…'}
                                        sx={acSx('#3b82f6')}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {loadingStates ? <CircularProgress size={14} sx={{ color: '#3b82f6' }} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                slotProps={{ paper: { sx: acPaperSx } }}
                                noOptionsText={<span style={{ color: '#555', fontSize: 13 }}>No states found</span>}
                            />
                        </Box>

                        <Button
                            variant="contained"
                            size="medium"
                            disabled={!cc || resolving}
                            onClick={handleResolve}
                            startIcon={resolving ? <CircularProgress size={14} /> : <SearchIcon sx={{ fontSize: 16 }} />}
                            sx={{
                                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                borderRadius: '10px', fontWeight: 700, fontSize: 14,
                                px: 3, height: 42, flexShrink: 0,
                                '&:hover': { filter: 'brightness(0.9)' },
                            }}
                        >
                            {resolving ? 'Checking…' : 'Check Fee'}
                        </Button>
                    </Box>

                    {result && (
                        <Box sx={{
                            mt: 2.5, p: 2.5, borderRadius: '10px',
                            background: '#3b82f608', border: '1px solid #3b82f630',
                            display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center',
                        }}>
                            <Box>
                                <Typography sx={{ ...labelSx, mb: 0.5, color: '#3b82f6' }}>Effective Fee</Typography>
                                <Typography sx={{ fontSize: 28, fontWeight: 900, color: '#3b82f6', letterSpacing: '-0.02em' }}>
                                    {result.feeType === 'percentage' ? `${result.fee}%` : `${result.currency ?? ''}${result.fee}`}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ ...labelSx, mb: 0.5 }}>Fee Type</Typography>
                                <Chip label={result.feeType} size="small" sx={{
                                    fontSize: 12, height: 24, background: '#3b82f615',
                                    color: '#3b82f6', border: '1px solid #3b82f630', textTransform: 'capitalize',
                                }} />
                            </Box>
                            <Box>
                                <Typography sx={{ ...labelSx, mb: 0.5 }}>Applied From</Typography>
                                <Chip
                                    label={result.source === 'state' ? 'State override' : result.source === 'country' ? 'Country default' : 'Global fallback'}
                                    size="small"
                                    sx={{
                                        fontSize: 12, height: 24,
                                        background: result.source === 'state' ? '#a78bfa15' : result.source === 'country' ? '#f2674a15' : '#5a5a5a15',
                                        color: result.source === 'state' ? '#a78bfa' : result.source === 'country' ? '#f2674a' : '#888',
                                        border: `1px solid ${result.source === 'state' ? '#a78bfa40' : result.source === 'country' ? '#f2674a40' : '#3a3a3a'}`,
                                    }}
                                />
                            </Box>
                            {/* {result.currency && (
                                <Box>
                                    <Typography sx={{ ...labelSx, mb: 0.5 }}>Currency</Typography>
                                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#c0c0c0' }}>{result.currency}</Typography>
                                </Box>
                            )} */}
                        </Box>
                    )}
                </Box>
            </Collapse>
        </Box>
    )
}


const acSx = (accent: string) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px', background: '#181818', color: '#e0e0e0', fontSize: 15, p: '2px 8px !important',
        '& fieldset': { borderColor: '#2f2f2f' },
        '&:hover fieldset': { borderColor: accent + '60' },
        '&.Mui-focused fieldset': { borderColor: accent },
    },
    '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': { color: '#5a5a5a' },
    '& .MuiInputBase-input': { fontSize: 14, color: '#e0e0e0', py: '7px !important' },
})

const acPaperSx = {
    background: '#1a1a1a',
    border: '1px solid #2f2f2f',
    borderRadius: '10px',
    '& .MuiAutocomplete-option': {
        fontSize: 14, color: '#d0d0d0', py: '8px',
        '&[aria-selected="true"]': { background: '#f2674a15' },
        '&.Mui-focused': { background: '#2a2a2a' },
    },
}

interface FeeFormProps {
    initial?: Partial<FeeFormData>
    countries: Country[]
    states: StateOption[]
    loadingStates: boolean
    onCountryChange: (code: string) => void
    fixedCountry?: string
    fixedState?: string | null
    onSubmit: (data: FeeFormData) => void
    onCancel: () => void
    loading: boolean
    accentColor?: string
    mode: 'country' | 'state' | 'edit'
}

const FeeForm: React.FC<FeeFormProps> = ({
    initial, countries, states, loadingStates, onCountryChange,
    fixedCountry, fixedState, onSubmit, onCancel, loading,
    accentColor = '#f2674a', mode,
}) => {
    const [form, setForm] = useState<FeeFormData>({
        countryCode: initial?.countryCode ?? '',
        stateCode: initial?.stateCode ?? '',
        feeType: initial?.feeType ?? 'percentage',
        fee: initial?.fee ?? '',
        currency: initial?.currency ?? '',
    })
    const [errors, setErrors] = useState<Partial<Record<keyof FeeFormData, string>>>({})

    const set = (key: keyof FeeFormData, val: string) => {
        setForm(f => ({ ...f, [key]: val }))
        setErrors(e => ({ ...e, [key]: '' }))
    }

    const validate = () => {
        const e: Partial<Record<keyof FeeFormData, string>> = {}
        if (!fixedCountry && !form.countryCode) e.countryCode = 'Required'
        if (mode === 'state' && fixedState === undefined && !form.stateCode) e.stateCode = 'Required'
        if (form.fee === '' || isNaN(Number(form.fee)) || Number(form.fee) < 0) e.fee = 'Enter a valid fee (≥ 0)'
        else if (form.feeType === 'percentage' && Number(form.fee) > 100) e.fee = 'Cannot exceed 100%'
        setErrors(e)
        return Object.keys(e).length === 0
    }

    const handleSubmit = () => {
        if (!validate()) return
        onSubmit({
            ...form,
            countryCode: fixedCountry ?? form.countryCode,
            stateCode: fixedState !== undefined ? (fixedState ?? '') : form.stateCode,
        })
    }

    const selectedCountry = countries.find(c => c.alpha2 === form.countryCode) ?? null
    const selectedState = states.find(s => s.code === form.stateCode) ?? null

    return (
        <Box sx={{
            background: '#161616', border: `1px solid ${accentColor}30`,
            borderRadius: '14px', p: 2.5, mt: 1,
        }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>

                {!fixedCountry && (
                    <Box sx={{ flex: '1 1 200px' }}>
                        <Typography sx={labelSx}>Country</Typography>
                        <Autocomplete
                            size="small"
                            options={countries}
                            getOptionLabel={c => c.name}
                            value={selectedCountry}
                            onChange={(_, val) => {
                                const code = val?.alpha2 ?? ''
                                set('countryCode', code)
                                set('stateCode', '')
                                onCountryChange(code)
                            }}
                            renderOption={(props, c) => (
                                <li {...props} key={c.alpha2}>
                                   <span style={{ color: '#5a5a5a', fontSize: 11, marginRight: 8, minWidth: 32 }}>{(c.alpha2)}</span>{c.name}
                                </li>
                            )}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    placeholder="Search country…"
                                    error={!!errors.countryCode}
                                    helperText={errors.countryCode}
                                    sx={acSx(accentColor)}
                                    // InputProps={{
                                    //     ...params.InputProps,
                                    //     startAdornment: selectedCountry
                                    //         ? <span style={{ fontSize: 18, marginRight: 6, flexShrink: 0 }}>{(selectedCountry.alpha2)}</span>
                                    //         : undefined,
                                    // }}
                                />
                            )}
                            slotProps={{ paper: { sx: acPaperSx } }}
                            noOptionsText={<span style={{ color: '#555', fontSize: 13 }}>No country found</span>}
                        />
                    </Box>
                )}

                {mode === 'state' && fixedState === undefined && (
                    <Box sx={{ flex: '1 1 200px' }}>
                        <Typography sx={labelSx}>State / Region</Typography>
                        <Autocomplete
                            size="small"
                            options={states}
                            getOptionLabel={s => s.name}
                            value={selectedState}
                            disabled={loadingStates || (!fixedCountry && !form.countryCode)}
                            loading={loadingStates}
                            onChange={(_, val) => set('stateCode', val?.code ?? '')}
                            renderOption={(props, s) => (
                                <li {...props} key={s.code}>
                                    <span style={{ color: '#5a5a5a', fontSize: 11, marginRight: 8, minWidth: 32 }}>{s.code}</span>
                                    {s.name}
                                </li>
                            )}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    placeholder={
                                        !fixedCountry && !form.countryCode
                                            ? 'Select country first'
                                            : loadingStates
                                                ? 'Loading states…'
                                                : 'Search state…'
                                    }
                                    error={!!errors.stateCode}
                                    helperText={errors.stateCode}
                                    sx={acSx(accentColor)}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingStates ? <CircularProgress size={14} sx={{ color: accentColor }} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            slotProps={{ paper: { sx: acPaperSx } }}
                            noOptionsText={<span style={{ color: '#555', fontSize: 13 }}>No states found</span>}
                        />
                    </Box>
                )}

                <Box sx={{ flex: '1 1 140px' }}>
                    <Typography sx={labelSx}>Fee Type</Typography>
                    <FormControl fullWidth size="small" sx={fieldStyle(accentColor)}>
                        {/* <Select value={form.feeType} onChange={e => set('feeType', e.target.value)}> */}
                        <Select value={form.feeType} onChange={e => set('feeType', 'percentage')}>
                            <MenuItem value="percentage" sx={{ fontSize: 14 }}>Percentage (%)</MenuItem>
                            {/* <MenuItem value="fixed" sx={{ fontSize: 14 }}>Fixed Amount ($)</MenuItem> */}
                        </Select>
                    </FormControl>
                </Box>

                <Box sx={{ flex: '1 1 130px' }}>
                    <Typography sx={labelSx}>Fee Amount</Typography>
                    <TextField
                        fullWidth size="small" type="number"
                        value={form.fee}
                        onChange={e => set('fee', e.target.value)}
                        error={!!errors.fee} helperText={errors.fee}
                        placeholder="0.00"
                        inputProps={{ min: 0, max: form.feeType === 'percentage' ? 100 : undefined, step: 0.01 }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Typography sx={{ color: accentColor, fontSize: 15, fontWeight: 700 }}>
                                        {form.feeType === 'percentage' ? '%' : '$'}
                                    </Typography>
                                </InputAdornment>
                            )
                        }}
                        sx={fieldStyle(accentColor)}
                    />
                </Box>

                {/* Currency */}
                {/* <Box sx={{ flex: '1 1 110px' }}>
                    <Typography sx={labelSx}>Currency</Typography>
                    <FormControl fullWidth size="small" sx={fieldStyle(accentColor)}>
                        <Select value={form.currency} onChange={e => set('currency', e.target.value)} displayEmpty
                            renderValue={v => v || <span style={{ color: '#444', fontSize: 14 }}>None</span>}>
                            <MenuItem value="" sx={{ fontSize: 14 }}>None</MenuItem>
                            {CURRENCIES.map(c => <MenuItem key={c} value={c} sx={{ fontSize: 14 }}>{c}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Box> */}
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, mt: 2, justifyContent: 'flex-end' }}>
                <Button size="medium" onClick={onCancel}
                    sx={{
                        color: '#6a6a6a', border: '1px solid #2a2a2a',
                        borderRadius: '10px', px: 2.5, fontSize: 13, fontWeight: 600,
                    }}>
                    Cancel
                </Button>
                <Button size="medium" variant="contained" disabled={loading} onClick={handleSubmit}
                    startIcon={loading ? <CircularProgress size={14} /> : <CheckIcon sx={{ fontSize: 16 }} />}
                    sx={{
                        background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)`,
                        borderRadius: '10px', fontWeight: 700, fontSize: 13, px: 3,
                        '&:hover': { filter: 'brightness(0.9)' },
                    }}>
                    {loading ? 'Saving…' : 'Save Fee'}
                </Button>
            </Box>
        </Box>
    )
}

interface StatePanelProps {
    group: CountryGroup
    onAdd: (data: FeeFormData, stateCode: string) => Promise<void>
    onEdit: (fee: PlatformFee, data: FeeFormData) => Promise<void>
    onRequestDelete: (target: DeleteTarget) => void
    onToggle: (fee: PlatformFee) => Promise<void>
    countries: Country[]
}

const StatePanel: React.FC<StatePanelProps> = ({
    group, onAdd, onEdit, onRequestDelete, onToggle, countries
}) => {
    const [addOpen, setAddOpen] = useState(false)
    const [editTarget, setEditTarget] = useState<PlatformFee | null>(null)
    const [states, setStates] = useState<StateOption[]>([])
    const [loadingStates, setLoadingStates] = useState(false)
    const [saving, setSaving] = useState(false)
    const [togglingId, setTogglingId] = useState<string | null>(null)

    useEffect(() => {
        let active = true
        setLoadingStates(true)
        ApiGetStates(group.countryCode)
            .then((res: any) => { if (active && res?.status) setStates(res.data ?? []) })
            .catch(() => { })
            .finally(() => { if (active) setLoadingStates(false) })
        return () => { active = false }
    }, [group.countryCode])

    const handleAdd = async (data: FeeFormData) => {
        if (!data.stateCode) return
        setSaving(true)
        await onAdd(data, data.stateCode)
        setSaving(false)
        setAddOpen(false)
    }

    const handleEdit = async (data: FeeFormData) => {
        if (!editTarget) return
        setSaving(true); await onEdit(editTarget, data); setSaving(false); setEditTarget(null)
    }

    const handleToggle = async (fee: PlatformFee) => {
        setTogglingId(fee._id)
        await onToggle(fee)
        setTogglingId(null)
    }

    const stateName = (code: string) => states.find(s => s.code === code)?.name ?? code

    return (
        <Box sx={{ px: 2.5, pb: 2.5 }}>
            {group.stateFees.length === 0 && !addOpen && (
                <Box sx={{
                    textAlign: 'center', py: 3, color: '#3a3a3a', fontSize: 13,
                    border: '1px dashed #222', borderRadius: '10px', mb: 1.5,
                }}>
                    No state overrides — country default applies everywhere.
                </Box>
            )}

            {group.stateFees.length > 0 && (
                <Box sx={{ border: '1px solid #1e1e1e', borderRadius: '10px', overflow: 'hidden', mb: 1.5 }}>
                    <Box sx={{
                        display: 'grid', gridTemplateColumns: '1fr 110px 80px 90px 120px',
                        px: 2.5, py: 1.2, background: '#161616', borderBottom: '1px solid #1e1e1e',
                    }}>
                        {['State / Region', 'Fee', 'Type', 'Actions'].map((h, i) => (
                            <Typography key={i} sx={{ fontSize: 10, color: '#838383', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700 }}>{h}</Typography>
                        ))}
                    </Box>

                    {group.stateFees.map((sf, idx) => (
                        <React.Fragment key={sf._id}>
                            {editTarget?._id === sf._id ? (
                                <Box sx={{ p: 2, borderBottom: idx < group.stateFees.length - 1 ? '1px solid #1a1a1a' : 'none' }}>
                                    <FeeForm
                                        mode="edit"
                                        initial={{ feeType: sf.feeType, fee: String(sf.fee), currency: sf.currency ?? '', stateCode: sf.stateCode ?? '' }}
                                        fixedCountry={group.countryCode}
                                        fixedState={sf.stateCode}
                                        countries={countries} states={states} loadingStates={loadingStates}
                                        onCountryChange={() => { }}
                                        onSubmit={handleEdit} onCancel={() => setEditTarget(null)}
                                        loading={saving} accentColor="#a78bfa"
                                    />
                                </Box>
                            ) : (
                                <Box sx={{
                                    display: 'grid', gridTemplateColumns: '1fr 110px 80px 90px 120px',
                                    px: 2.5, py: 1.8, alignItems: 'center',
                                    borderBottom: idx < group.stateFees.length - 1 ? '1px solid #1a1a1a' : 'none',
                                    '&:hover': { background: '#141414' },
                                    opacity: sf.isActive ? 1 : 0.45, transition: 'opacity 0.2s',
                                }}>
                                    <Box>
                                        <Typography sx={{ fontSize: 14, color: '#c0c0c0', fontWeight: 600 }}>
                                            {stateName(sf.stateCode ?? '')}
                                        </Typography>
                                        <Typography sx={{ fontSize: 11, color: '#838383', mt: 0.2 }}>{sf.stateCode}</Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: 15, color: '#a78bfa', fontWeight: 800 }}>
                                        {feeLabel(sf)}
                                    </Typography>
                                    <Chip label={sf.feeType === 'percentage' ? '%' : '$'} size="small" sx={{
                                        fontSize: 10, height: 20, width: 34,
                                        background: '#a78bfa15', color: '#a78bfa', border: '1px solid #a78bfa30',
                                    }} />
                                    {/* <Typography sx={{ fontSize: 13, color: '#4a4a4a' }}>{sf.currency ?? '—'}</Typography> */}
                                    <Box sx={{ display: 'flex', gap: 0.3, alignItems: 'center' }}>
                                        {togglingId === sf._id
                                            ? <CircularProgress size={16} sx={{ color: '#a78bfa', mx: 1 }} />
                                            : (
                                                <Tooltip title={sf.isActive ? 'Deactivate' : 'Activate'} arrow>
                                                    <Switch size="small" checked={sf.isActive}
                                                        onChange={() => handleToggle(sf)}
                                                        sx={{
                                                            '& .Mui-checked .MuiSwitch-thumb': { background: '#a78bfa' },
                                                            '& .Mui-checked + .MuiSwitch-track': { background: '#a78bfa40' },
                                                        }} />
                                                </Tooltip>
                                            )
                                        }
                                        <Tooltip title="Edit" arrow>
                                            <IconButton size="small" onClick={() => setEditTarget(sf)}
                                                sx={{ color: '#f2674a', '&:hover': { color: '#a78bfa' }, p: 0.6 }}>
                                                <EditIcon sx={{ fontSize: 15 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete" arrow>
                                            <IconButton size="small"
                                                onClick={() => onRequestDelete({ fee: sf, label: `${stateName(sf.stateCode ?? '')} (${group.countryCode})` })}
                                                sx={{ color: 'red', '&:hover': { color: '#ef4444' }, p: 0.6 }}>
                                                <DeleteIcon sx={{ fontSize: 15 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            )}
                        </React.Fragment>
                    ))}
                </Box>
            )}

            {addOpen ? (
                <FeeForm
                    mode="state"
                    initial={{ countryCode: group.countryCode }}
                    fixedCountry={group.countryCode}
                    countries={countries} states={states} loadingStates={loadingStates}
                    onCountryChange={() => { }}
                    onSubmit={handleAdd}
                    onCancel={() => setAddOpen(false)}
                    loading={saving} accentColor="#a78bfa"
                />
            ) : (
                <Button size="medium" startIcon={<AddIcon sx={{ fontSize: 15 }} />}
                    onClick={() => setAddOpen(true)}
                    sx={{
                        color: '#a78bfa', fontSize: 13, fontWeight: 700, mt: 0.5,
                        border: '1px dashed #a78bfa40', borderRadius: '10px', px: 2.5, py: 1,
                        '&:hover': { background: '#a78bfa08', borderColor: '#a78bfa70' },
                    }}>
                    Add State Fee Override
                </Button>
            )}
        </Box>
    )
}

interface CountryRowProps {
    group: CountryGroup
    expanded: boolean
    onExpand: () => void
    onEdit: (fee: PlatformFee, data: FeeFormData) => Promise<void>
    onRequestDelete: (target: DeleteTarget) => void
    onToggle: (fee: PlatformFee) => Promise<void>
    onAddState: (data: FeeFormData, stateCode: string) => Promise<void>
    onEditState: (fee: PlatformFee, data: FeeFormData) => Promise<void>
    onRequestDeleteState: (target: DeleteTarget) => void
    onToggleState: (fee: PlatformFee) => Promise<void>
    countries: Country[]
    isLast: boolean
}

const CountryRow: React.FC<CountryRowProps> = ({
    group, expanded, onExpand, onEdit, onRequestDelete, onToggle,
    onAddState, onEditState, onRequestDeleteState, onToggleState, countries, isLast,
}) => {
    const [editOpen, setEditOpen] = useState(false)
    const [saving, setSaving] = useState(false)
    const [toggling, setToggling] = useState(false)
    const df = group.defaultFee

    const handleEdit = async (data: FeeFormData) => {
        if (!df) return
        setSaving(true); await onEdit(df, data); setSaving(false); setEditOpen(false)
    }

    const handleToggle = async () => {
        if (!df) return
        setToggling(true); await onToggle(df); setToggling(false)
    }

    return (
        <Box sx={{ borderBottom: isLast ? 'none' : '1px solid #1e1e1e' }}>
            <Box sx={{
                display: 'grid', gridTemplateColumns: '1fr 150px 110px 140px 160px',
                px: 3, py: 2.2, alignItems: 'center',
                '&:hover': { background: '#191919' },
                opacity: df && !df.isActive ? 0.5 : 1,
                transition: 'opacity 0.2s',
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box>
                        <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#d0d0d0' }}>{group.countryName}</Typography>
                        <Typography sx={{ fontSize: 11, color: '#7d7d7d', letterSpacing: '0.05em', mt: 0.2 }}>{group.countryCode}</Typography>
                    </Box>
                </Box>

                <Typography sx={{ fontSize: 17, fontWeight: 900, color: '#f2674a', letterSpacing: '-0.01em' }}>
                    {df ? feeLabel(df) : <span style={{ color: '#2f2f2f', fontWeight: 400, fontSize: 13 }}>Not set</span>}
                </Typography>

                {df ? (
                    <Chip label={df.feeType} size="small" sx={{
                        fontSize: 11, height: 22, width: 'fit-content', textTransform: 'capitalize',
                        background: '#f2674a15', color: '#f2674a', border: '1px solid #f2674a30',
                    }} />
                ) : <Box />}

                <Box onClick={onExpand} sx={{
                    display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', width: 'fit-content',
                    color: expanded ? '#a78bfa' : '#555',
                    '&:hover': { color: '#a78bfa' }, transition: 'color 0.15s',
                }}>
                    {group.stateFees.length > 0 && (
                        <Box sx={{
                            fontSize: 11, fontWeight: 700, px: 1.2, py: 0.4, borderRadius: '6px',
                            background: expanded ? '#a78bfa20' : '#2a2a2a',
                            color: expanded ? '#a78bfa' : '#555', border: `1px solid ${expanded ? '#a78bfa40' : '#333'}`,
                            minWidth: 26, textAlign: 'center',
                        }}>
                            {group.stateFees.length}
                        </Box>
                    )}
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                        {group.stateFees.length > 0 ? 'state overrides' : 'Add state fees'}
                    </Typography>
                    {expanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {df && (
                        toggling
                            ? <CircularProgress size={18} sx={{ color: '#f2674a', mx: 1 }} />
                            : (
                                <Tooltip title={df.isActive ? 'Deactivate' : 'Activate'} arrow>
                                    <Switch size="small" checked={df.isActive} onChange={handleToggle}
                                        sx={{
                                            '& .Mui-checked .MuiSwitch-thumb': { background: '#f2674a' },
                                            '& .Mui-checked + .MuiSwitch-track': { background: '#f2674a40' },
                                        }} />
                                </Tooltip>
                            )
                    )}
                    {df && (
                        <Tooltip title="Edit fee" arrow>
                            <IconButton size="small" onClick={() => setEditOpen(e => !e)}
                                sx={{ color: '#f2674a', '&:hover': { color: '#f2674a' }, p: 0.6 }}>
                                <EditIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                    {df && (
                        <Tooltip title="Delete" arrow>
                            <IconButton size="small"
                                onClick={() => onRequestDelete({ fee: df, label: group.countryName })}
                                sx={{ color: 'red', '&:hover': { color: '#ef4444' }, p: 0.6 }}>
                                <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </Box>

            {editOpen && df && (
                <Box sx={{ px: 3, pb: 2 }}>
                    <FeeForm
                        mode="edit"
                        initial={{ feeType: df.feeType, fee: String(df.fee), currency: df.currency ?? '' }}
                        fixedCountry={group.countryCode} fixedState={null}
                        countries={countries} states={[]} loadingStates={false}
                        onCountryChange={() => { }}
                        onSubmit={handleEdit} onCancel={() => setEditOpen(false)}
                        loading={saving} accentColor="#f2674a"
                    />
                </Box>
            )}

            <Collapse in={expanded} timeout={200}>
                <Box sx={{
                    ml: 6, mr: 2.5, mb: 2, mt: 0.5,
                    borderLeft: '2px solid #252525',
                    background: '#111', borderRadius: '0 12px 12px 0',
                }}>
                    <Box sx={{
                        px: 2.5, py: 1.2, borderBottom: '1px solid #1a1a1a',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}>
                        <Box>
                            <Typography sx={{ fontSize: 11, color: '#838383', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700 }}>
                                State / Region Overrides — {group.countryName}
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: '#838383', mt: 0.3 }}>
                                State fees take priority over the country default above
                            </Typography>
                        </Box>
                    </Box>
                    <StatePanel
                        group={group}
                        onAdd={onAddState}
                        onEdit={onEditState}
                        onRequestDelete={onRequestDeleteState}
                        onToggle={onToggleState}
                        countries={countries}
                    />
                </Box>
            </Collapse>
        </Box>
    )
}

interface FilterBarProps {
    countries: Country[]
    onChange: (qs: string) => void
    loading: boolean
    onRefresh: () => void
}

const FilterBar: React.FC<FilterBarProps> = ({ countries, onChange, loading, onRefresh }) => {
    const [cc, setCc] = useState('')
    const [feeType, setFeeType] = useState('')
    const [isActive, setIsActive] = useState('')

    const apply = (overrides?: any) => {
        const params = { countryCode: cc, feeType, isActive, ...overrides }
        onChange(buildQS({ ...params, limit: 200 }))
    }

    const reset = () => {
        setCc(''); setFeeType(''); setIsActive('')
        onChange(buildQS({ limit: 200 }))
    }

    const hasFilter = cc || feeType || isActive !== ''

    return (
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 2.5 }}>
            <Autocomplete
                size="small"
                options={countries}
                getOptionLabel={c => c.name}
                value={countries.find(c => c.alpha2 === cc) ?? null}
                onChange={(_, val) => { setCc(val?.alpha2 ?? ''); apply({ countryCode: val?.alpha2 ?? '' }) }}
                sx={{ minWidth: 200 }}
                renderOption={(props, c) => (
                    <li {...props} key={c.alpha2}>
                       <span style={{ color: '#5a5a5a', fontSize: 11, marginRight: 8, minWidth: 32 }}>{(c.alpha2)}</span>{c.name}
                    </li>
                )}
                renderInput={params => (
                    <TextField
                        {...params}
                        placeholder="All Countries"
                        sx={acSx('#f2674a')}
                        // InputProps={{
                        //     ...params.InputProps,
                        //     startAdornment: cc
                        //         ? <span style={{ fontSize: 16, marginRight: 6, flexShrink: 0 }}>{(cc)}</span>
                        //         : undefined,
                        // }}
                    />
                )}
                slotProps={{ paper: { sx: acPaperSx } }}
                noOptionsText={<span style={{ color: '#555', fontSize: 13 }}>No country found</span>}
            />

            {/* <FormControl size="small" sx={{ minWidth: 140, ...fieldStyle('#f2674a') }}>
                <Select value={feeType} onChange={e => { setFeeType(e.target.value); apply({ feeType: e.target.value }) }}
                    displayEmpty renderValue={v => v || <span style={{ color: '#444', fontSize: 13 }}>All Types</span>}>
                    <MenuItem value="" sx={{ fontSize: 14 }}>All Types</MenuItem>
                    <MenuItem value="percentage" sx={{ fontSize: 14 }}>Percentage</MenuItem>
                    <MenuItem value="fixed" sx={{ fontSize: 14 }}>Fixed</MenuItem>
                </Select>
            </FormControl> */}

            <FormControl size="small" sx={{ minWidth: 130, ...fieldStyle('#f2674a') }}>
                <Select value={isActive} onChange={e => { setIsActive(e.target.value); apply({ isActive: e.target.value }) }}
                    displayEmpty renderValue={v => v === '' ? <span style={{ color: '#444', fontSize: 13 }}>All Status</span> : v === 'true' ? 'Active' : 'Inactive'}>
                    <MenuItem value="" sx={{ fontSize: 14 }}>All Status</MenuItem>
                    <MenuItem value="true" sx={{ fontSize: 14 }}>Active</MenuItem>
                    <MenuItem value="false" sx={{ fontSize: 14 }}>Inactive</MenuItem>
                </Select>
            </FormControl>

            {hasFilter && (
                <Button size="small" onClick={reset}
                    sx={{ color: '#6a6a6a', fontSize: 13, border: '1px solid #2a2a2a', borderRadius: '10px', px: 2, py: 0.8 }}>
                    Clear Filters
                </Button>
            )}

            <Box sx={{ ml: 'auto' }}>
                <Tooltip title="Refresh" arrow>
                    <IconButton size="medium" onClick={onRefresh} disabled={loading}
                        sx={{ color: '#3a3a3a', '&:hover': { color: '#f2674a' } }}>
                        {loading ? <CircularProgress size={18} sx={{ color: '#f2674a' }} /> : <RefreshIcon sx={{ fontSize: 20 }} />}
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    )
}

const PlatformFeesSection: React.FC = () => {
    const [groups, setGroups] = useState<CountryGroup[]>([])
    const [loading, setLoading] = useState(true)
    const [countries, setCountries] = useState<Country[]>([])
    const [expandedCountry, setExpandedCountry] = useState<string | null>(null)
    const [addingCountry, setAddingCountry] = useState(false)
    const [addCountryStates, setAddCountryStates] = useState<StateOption[]>([])
    const [loadingAddStates, setLoadingAddStates] = useState(false)
    const [savingAdd, setSavingAdd] = useState(false)
    const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null)
    const [deleting, setDeleting] = useState(false)
    const [currentQS, setCurrentQS] = useState(buildQS({ limit: 200 }))

    const [errorPopup, setErrorPopup] = useState<{ open: boolean; message: string }>({ open: false, message: '' })
    const [successPopup, setSuccessPopup] = useState<{ open: boolean; message: string }>({ open: false, message: '' })

    const showError = (msg: string) => setErrorPopup({ open: true, message: msg })
    const showSuccess = (msg: string) => setSuccessPopup({ open: true, message: msg })

    const fetchFees = useCallback(async (qs?: string) => {
        setLoading(true)
        try {
            const res = await ApiListPlatformFees(qs ?? currentQS)
            if (res?.status) setGroups(res.data ?? [])
            else showError(res?.[0]?.message || 'Failed to fetch fees')
        } catch (e: any) {
            showError(e?.response?.data?.message || e?.[0] || 'Failed to fetch fees')
        } finally { setLoading(false) }
    }, [currentQS])

    const fetchCountries = useCallback(async () => {
        try {
            const res = await ApiGetCountries()
            if (res?.status) setCountries(res.data ?? [])
        } catch (e: any) {
            showError(e?.response?.data?.message || e?.[0] || 'Failed to fetch countries')
        }
    }, [])

    useEffect(() => { fetchFees(); fetchCountries() }, [fetchFees, fetchCountries])

    const handleFilterChange = (qs: string) => {
        setCurrentQS(qs)
        fetchFees(qs)
    }

    const handleCreate = async (data: FeeFormData, stateCode: string | null = null) => {
        try {
            const payload: any = {
                countryCode: data.countryCode,
                feeType: data.feeType,
                fee: Number(data.fee),
                ...(data.currency && { currency: data.currency }),
                isActive: true,
                stateCode: stateCode,
            }

            const res = await ApiCreatePlatformFee(payload)
            if (res?.status) {
                showSuccess(stateCode
                    ? `State fee for ${stateCode} (${data.countryCode}) created`
                    : `Country fee for ${data.countryCode} created`
                )
                await fetchFees()
            } else {
                showError(res?.[0]?.message || 'Failed to create fee')
            }
        } catch (e: any) {
            showError(e?.response?.data?.message || e?.[0] || 'Failed to create fee')
        }
    }

    const handleAddCountryFee = async (data: FeeFormData) => {
        setSavingAdd(true)
        await handleCreate(data, null)
        setSavingAdd(false)
        setAddingCountry(false)
    }

    const handleAddCountryFormCountryChange = async (code: string) => {
        if (!code) return
        setLoadingAddStates(true)
        try {
            const res = await ApiGetStates(code)
            if (res?.status) setAddCountryStates(res.data ?? [])
        } catch { } finally { setLoadingAddStates(false) }
    }

    const handleEdit = async (fee: PlatformFee, data: FeeFormData): Promise<void> => {
        try {
            const res = await ApiUpdatePlatformFee(fee._id, {
                feeType: data.feeType,
                fee: Number(data.fee),
                currency: data.currency || null,
            })
            if (res?.status) {
                showSuccess('Fee updated')
                await fetchFees()
            } else {
                showError(res?.[0]?.message || 'Failed to update fee')
            }
        } catch (e: any) {
            showError(e?.response?.data?.message || e?.[0] || 'Failed to update fee')
        }
    }

    const handleToggle = async (fee: PlatformFee): Promise<void> => {
        try {
            const res = await ApiUpdatePlatformFeeStatus(fee._id, { isActive: !fee.isActive })
            if (res?.status) {
                await fetchFees()
            } else {
                showError(res?.[0]?.message || 'Failed to update status')
            }
        } catch (e: any) {
            console.log("Error toggling fee status:", e)
            showError(e?.response?.data?.message || e?.[0] || 'Failed to update status')
        }
    }

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return
        setDeleting(true)
        try {
            const res = await ApiDeletePlatformFee(deleteTarget.fee._id)
            if (res?.status) {
                showSuccess('Fee deleted')
                await fetchFees()
            } else {
                showError(res?.[0]?.message || 'Failed to delete fee')
            }
        } catch (e: any) {
            showError(e?.response?.data?.message || e?.[0] || 'Failed to delete fee')
        } finally { setDeleting(false); setDeleteTarget(null) }
    }

    const toggleExpand = (code: string) =>
        setExpandedCountry(prev => prev === code ? null : code)

    return (
        <>
            <Box className="bg-dark-color border-10 p-4" sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.8 }}>
                            <Box sx={{
                                width: 34, height: 34, borderRadius: '10px',
                                background: '#f2674a18', border: '1px solid #f2674a30',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <PublicIcon sx={{ color: '#f2674a', fontSize: 18 }} />
                            </Box>
                            <Typography fontSize={17} fontWeight={800} color="#e0e0e0">
                                Country &amp; State Fees
                            </Typography>
                        </Box>
                        <Typography fontSize={13} color="#5a5a5a" ml={0.5} lineHeight={1.6}>
                            Set a fee per country, then optionally override it for specific states.
                            State fees take priority over country fees. If neither is set, the global default applies.
                        </Typography>
                    </Box>
                    {!addingCountry && (
                        <Button size="medium" variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() => setAddingCountry(true)}
                            sx={{
                                borderColor: '#f2674a50', color: '#f2674a', ml: 2,
                                borderRadius: '12px', fontWeight: 700, fontSize: 13, px: 2.5, py: 1,
                                flexShrink: 0,
                                '&:hover': { borderColor: '#f2674a', background: '#f2674a0a' },
                            }}>
                            Add Country
                        </Button>
                    )}
                </Box>

                <Divider sx={{ borderColor: '#2a2a2a', mb: 2.5 }} />

                {addingCountry && (
                    <Box sx={{ mb: 2.5 }}>
                        <Typography sx={{ ...labelSx, mb: 1.2 }}>New Country Fee</Typography>
                        <FeeForm
                            mode="country"
                            countries={countries}
                            states={addCountryStates}
                            loadingStates={loadingAddStates}
                            onCountryChange={handleAddCountryFormCountryChange}
                            onSubmit={handleAddCountryFee}
                            onCancel={() => { setAddingCountry(false); setAddCountryStates([]) }}
                            loading={savingAdd} accentColor="#f2674a"
                        />
                        <Divider sx={{ borderColor: '#1e1e1e', mt: 2.5, mb: 1.5 }} />
                    </Box>
                )}

                <FilterBar
                    countries={countries}
                    onChange={handleFilterChange}
                    loading={loading}
                    onRefresh={() => fetchFees()}
                />

                {loading ? (
                    <Box sx={{ border: '1px solid #1e1e1e', borderRadius: '14px', overflow: 'hidden' }}>
                        {[...Array(3)].map((_, i) => (
                            <Box key={i} sx={{ px: 3, py: 2.5, borderBottom: i < 2 ? '1px solid #1a1a1a' : 'none', display: 'flex', gap: 5 }}>
                                <Skeleton variant="text" width={160} height={22} sx={{ bgcolor: '#1e1e1e' }} />
                                <Skeleton variant="text" width={70} height={22} sx={{ bgcolor: '#1e1e1e' }} />
                                <Skeleton variant="text" width={70} height={22} sx={{ bgcolor: '#1e1e1e' }} />
                                <Skeleton variant="text" width={90} height={22} sx={{ bgcolor: '#1e1e1e' }} />
                            </Box>
                        ))}
                    </Box>
                ) : groups.length === 0 ? (
                    <Box sx={{
                        textAlign: 'center', py: 7,
                        border: '1px dashed #2a2a2a', borderRadius: '14px',
                    }}>
                        <PublicIcon sx={{ fontSize: 38, mb: 1.5, color: '#2a2a2a' }} />
                        <Typography fontSize={15} color="#3a3a3a">No country fees configured.</Typography>
                        <Typography fontSize={13} color="#2a2a2a" mt={0.8}>Global fee applies to all users by default.</Typography>
                    </Box>
                ) : (
                    <Box sx={{ border: '1px solid #1e1e1e', borderRadius: '14px', overflow: 'hidden' }}>
                        <Box sx={{
                            display: 'grid', gridTemplateColumns: '1fr 150px 110px 140px 160px',
                            px: 3, py: 1.5, background: '#1a1a1a', borderBottom: '1px solid #1e1e1e',
                        }}>
                            {['Country', 'Default Fee', 'Type', 'State Fees', 'Actions'].map((h, i) => (
                                <Typography key={i} sx={{ fontSize: 10, color: '#878484', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700 }}>{h}</Typography>
                            ))}
                        </Box>

                        {groups.map((group, idx) => (
                            <CountryRow
                                key={group.countryCode}
                                group={group}
                                expanded={expandedCountry === group.countryCode}
                                onExpand={() => toggleExpand(group.countryCode)}
                                onEdit={handleEdit}
                                onRequestDelete={setDeleteTarget}
                                onToggle={handleToggle}
                                onAddState={(data, stateCode) => handleCreate(data, stateCode)}
                                onEditState={handleEdit}
                                onRequestDeleteState={setDeleteTarget}
                                onToggleState={handleToggle}
                                countries={countries}
                                isLast={idx === groups.length - 1}
                            />
                        ))}
                    </Box>
                )}

                <ResolvePanel countries={countries} />
            </Box>

            <PopupDialog
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDeleteConfirm}
                loading={deleting}
                variant="danger"
                title="Delete Fee"
                message={deleteTarget ? `Remove the fee entry for ${deleteTarget.label}? This cannot be undone.` : ''}
                confirmText="Delete"
                cancelText="Cancel"
            />

            <PopupDialog
                open={errorPopup.open}
                onClose={() => setErrorPopup({ open: false, message: '' })}
                onConfirm={() => setErrorPopup({ open: false, message: '' })}
                variant="error"
                title="Something Went Wrong"
                message={errorPopup.message}
                confirmText="Dismiss"
            />

            <PopupDialog
                open={successPopup.open}
                onClose={() => setSuccessPopup({ open: false, message: '' })}
                onConfirm={() => setSuccessPopup({ open: false, message: '' })}
                variant="success"
                title="Success"
                message={successPopup.message}
                confirmText="Done"
            />
        </>
    )
}

export default PlatformFeesSection